import {DrawerRootProps as ChakraDrawerProps, DrawerOpenChangeDetails, DrawerContentProps} from "@chakra-ui/react";
import {ComponentType, FC, Suspense, useCallback} from "react";
import Loading from "../components/Loading";
import {useDialogDiagnostics} from "../utils/diagnostics";
import {DrawerContent, DrawerRoot} from "../components/ui/drawer";

/** Additional props passed to your component when wrapping with `withDialog`. */
export type InnerDrawerProps = {
  /** Call this to close the dialog. */
  onClose: () => void;
};

/** Props added to a component when wrapped with `withDialog`. */
type WithDrawerExtraProps = {
  /** Determines whether the dialog is open. */
  open: boolean;

  /** Called when the dialog is closed. */
  onClose: () => void;

  /** Called when the dialog has fully exited. */
  onExitComplete?: () => void;
};

/** These props should not be defined on the inner dialog component passed to
 * `withDialog`, because they conflict with the added outer props. */
type RestrictedDrawerProps = {[K in keyof Omit<WithDrawerExtraProps, keyof InnerDrawerProps>]?: never};

/** The final props on the component returned by `withDialog<P>(...)` */
type WithDrawerProps<P> = WithDrawerExtraProps & Omit<P, keyof InnerDrawerProps | keyof RestrictedDrawerProps>;

/** Other props passed as a 2nd argument to `withDialog` to customize the dialog. */
type OtherDrawerProps = Omit<ChakraDrawerProps, keyof WithDrawerExtraProps | "children"> & {
  contentProps?: Omit<DrawerContentProps, "children">;
};

export function withDrawer<P extends InnerDrawerProps & RestrictedDrawerProps>(
  WrappedComponent: ComponentType<P>,
  otherProps?: OtherDrawerProps,
): FC<WithDrawerProps<P>> {
  return ({open, onClose, onExitComplete, ...rest}: WithDrawerProps<P>) => {
    const diagnosticsRef = useDialogDiagnostics(open);

    const onOpenChange = useCallback(
      (details: DrawerOpenChangeDetails) => {
        if (!details.open) {
          onClose.call(null);
        }
      },
      [onClose],
    );

    const innerDrawerProps: InnerDrawerProps = {onClose};

    const {contentProps, ...rootProps} = otherProps ?? {};

    return (
      <DrawerRoot open={open} onOpenChange={onOpenChange} onExitComplete={onExitComplete} {...rootProps}>
        <DrawerContent ref={diagnosticsRef} {...contentProps}>
          <Suspense fallback={<Loading m={8} />}>
            <WrappedComponent {...(rest as unknown as P)} {...innerDrawerProps} />
          </Suspense>
        </DrawerContent>
      </DrawerRoot>
    );
  };
}
